<template>
  <div class="uploadMask" @drop="dropHandle">
    <div class="uploadMask-content">
      <div class="uploadMask-text"><UploadOutlined class="icon" />在此处拖放文件</div>
      <div class="uploadMask-tips" v-if="props.tips?.length">{{ props.tips }}</div>
    </div>
  </div>
</template>

<script setup>
import { UploadOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { uploadFile } from '@/service/api/app'
import { reactive } from 'vue'
import { isUploading, lockUpload, unlockUpload } from '../uploadLock'

const props = defineProps({
  // 上传文件类型，对应数组内的type的值
  types: {
    type: Array,
    default: () => []
  },
  // 提示文本
  tips: {
    type: String,
    default: ''
  },
  // 最大上传文件大小MB
  maxSizeMB: {
    type: [Number, null],
    default: null
  },
  // 如果上传的是图片限制图片的宽度px[min, max]
  widthScope: {
    type: [Array, null],
    default: null
  },
  // 如果上传的是图片限制图片的高度px[min, max]
  heightScope: {
    type: [Array, null],
    default: null
  },
  // 宽高比限制['16:9', '4:3']
  ratio: {
    type: [Array, null],
    default: null
  },
  // 是否用内置上传方法
  useUpload: {
    type: Boolean,
    default: true
  },
  // 单次最多上传个数
  maxLength: {
    type: Number,
    default: 5
  },
  // 上传前校验函数
  // 返回 false / 其它假值：取消本次上传
  // 返回数组：用该数组替换待上传文件（可用于「过滤掉不合规文件后继续上传」）
  // 返回 true：原样继续
  before: {
    type: [Function, null],
    default: null
  },
  // 是否开启粘贴上传（Ctrl/Cmd + V）
  paste: {
    type: Boolean,
    default: true
  },
  /* 静默模式：不弹「上传中 / 上传成功」的全局提示，由调用方自己在界面上呈现进度
     （失败提示仍然弹——那是必须让用户看见的）。默认 false，老的调用方行为不变 */
  silent: {
    type: Boolean,
    default: false
  }
})
// uploading：上传开始 / 结束各抛一次，静默模式下调用方靠它显示自己的上传中状态
const emits = defineEmits(['success', 'error', 'uploading'])

// 后端定义文件类型映射
const fileTypeMap = {
  IMAGE: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'ico', 'svg'],
  VIDEO: [
    'mp4',
    'avi',
    'mov',
    'wmv',
    'mkv',
    'flv',
    'mpeg',
    'mpg',
    'm4v',
    '3gp',
    'asf',
    'divx',
    'xvid',
    'rm',
    'rmvb',
    'vob',
    'webm'
  ],
  TEXT: [
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'md',
    'csv',
    'pdf',
    'txt',
    'msg',
    'epub',
    'html',
    'htm',
    'xml'
  ],
  AUDIO: ['mp3', 'wav', 'aiff', 'aac', 'ogg', 'flac', 'alac', 'ape', 'm4a', 'm3u8', 'tts']
}

const uploadStates = reactive({
  total: 0,
  success: 0
})

// 根据文件扩展名获取bizType
const getBizTypeByExtension = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase()
  for (const [bizType, extensions] of Object.entries(fileTypeMap)) {
    if (extensions.includes(extension)) {
      return bizType
    }
  }
  // 如果找不到匹配的类型，默认返回IMAGE
  return 'IMAGE'
}

// 校验文件类型和大小
const checkFile = (files) => {
  // 遍历所有文件进行校验
  for (let file of files) {
    // 校验文件类型
    if (props.types && props.types.length > 0) {
      const fileExtension = file.name.split('.').pop().toLowerCase()
      const fileMimeType = file.type

      // 检查文件扩展名或MIME类型是否匹配
      const isValidType = props.types.some((type) => {
        // 如果配置的是扩展名格式（如 'jpg', 'png'）
        if (!type.includes('/')) {
          const cleanType = type.toLowerCase().replace('.', '')
          return fileExtension === cleanType
        } else {
          // 如果配置的是MIME类型格式（如 'image/jpeg', 'application/pdf'）
          return fileMimeType.includes(type)
        }
      })

      if (!isValidType) {
        const errorMsg = `文件 "${file.name}" 类型不支持`
        message.error(errorMsg)
        emits('error', { message: errorMsg, file })
        return false
      }
    }

    // 校验文件大小
    if (props.maxSizeMB !== null && props.maxSizeMB > 0) {
      const fileSizeMB = file.size / 1024 / 1024 // 转换为MB
      if (fileSizeMB > props.maxSizeMB) {
        const errorMsg = `文件 "${file.name}" 大小超过 ${props.maxSizeMB}MB 限制`
        message.error(errorMsg)
        emits('error', { message: errorMsg, file })
        return false
      }
    }

    // 检查是否是图片文件，如果是且有尺寸或比例限制，则进行校验
    const isImage = file.type.startsWith('image/')
    if (isImage && (props.widthScope || props.heightScope || props.ratio)) {
      // 对于图片文件，需要异步校验尺寸和比例
      return new Promise((resolve) => {
        const img = new Image()
        const objectUrl = URL.createObjectURL(file)

        img.onload = () => {
          // 校验宽度范围
          if (
            props.widthScope &&
            Array.isArray(props.widthScope) &&
            props.widthScope.length === 2
          ) {
            const [minWidth, maxWidth] = props.widthScope
            if (minWidth !== null && img.width < minWidth) {
              const errorMsg = `图片 "${file.name}" 宽度(${img.width}px) 不能小于 ${minWidth}px`
              message.error(errorMsg)
              emits('error', { message: errorMsg, file })
              URL.revokeObjectURL(objectUrl)
              resolve(false)
              return
            }

            if (maxWidth !== null && img.width > maxWidth) {
              const errorMsg = `图片 "${file.name}" 宽度(${img.width}px) 不能大于 ${maxWidth}px`
              message.error(errorMsg)
              emits('error', { message: errorMsg, file })
              URL.revokeObjectURL(objectUrl)
              resolve(false)
              return
            }
          }

          // 校验高度范围
          if (
            props.heightScope &&
            Array.isArray(props.heightScope) &&
            props.heightScope.length === 2
          ) {
            const [minHeight, maxHeight] = props.heightScope
            if (minHeight !== null && img.height < minHeight) {
              const errorMsg = `图片 "${file.name}" 高度(${img.height}px) 不能小于 ${minHeight}px`
              message.error(errorMsg)
              emits('error', { message: errorMsg, file })
              URL.revokeObjectURL(objectUrl)
              resolve(false)
              return
            }

            if (maxHeight !== null && img.height > maxHeight) {
              const errorMsg = `图片 "${file.name}" 高度(${img.height}px) 不能大于 ${maxHeight}px`
              message.error(errorMsg)
              emits('error', { message: errorMsg, file })
              URL.revokeObjectURL(objectUrl)
              resolve(false)
              return
            }
          }

          // 校验宽高比
          if (props.ratio && Array.isArray(props.ratio) && props.ratio.length > 0) {
            const currentRatio = img.width / img.height

            // 检查当前宽高比是否匹配任意一个指定的宽高比
            const isValidRatio = props.ratio.some((ratioStr) => {
              if (typeof ratioStr === 'string') {
                const [ratioWidthStr, ratioHeightStr] = ratioStr.split(':')
                if (ratioWidthStr && ratioHeightStr) {
                  const ratioWidth = parseFloat(ratioWidthStr)
                  const ratioHeight = parseFloat(ratioHeightStr)
                  if (
                    !isNaN(ratioWidth) &&
                    !isNaN(ratioHeight) &&
                    ratioWidth > 0 &&
                    ratioHeight > 0
                  ) {
                    const expectedRatio = ratioWidth / ratioHeight
                    // 考虑浮点数精度，允许小范围误差
                    const tolerance = 0.01
                    return Math.abs(currentRatio - expectedRatio) <= tolerance
                  }
                }
              }
              return false
            })

            if (!isValidRatio) {
              const errorMsg = `图片 "${file.name}" 宽高比(${img.width}:${
                img.height
              })不符合要求(${props.ratio.join(', ')})`
              message.error(errorMsg)
              emits('error', { message: errorMsg, file })
              URL.revokeObjectURL(objectUrl)
              resolve(false)
              return
            }
          }

          // 所有校验通过
          URL.revokeObjectURL(objectUrl)
          resolve(true)
        }

        img.onerror = () => {
          // 如果无法加载图片进行尺寸检查，则跳过尺寸校验
          URL.revokeObjectURL(objectUrl)
          resolve(true)
        }

        img.src = objectUrl
      })
    }
  }

  // 所有文件都通过校验
  return true
}

/**
 * 拖拽上传处理函数
 * 处理拖拽事件中的文件，根据配置进行文件校验并执行上传
 * @param {DragEvent} e - 拖拽事件对象
 * @returns {Promise<void>} 异步处理结果
 */
const dropHandle = async (e) => {
  // 获取拖拽的文件列表（e.dataTransfer.files 是 FileList 对象）
  const items = e.dataTransfer.items
  let files = []

  // 如果支持webkitGetAsEntry API，可以处理文件夹
  if (items && items.length > 0) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i].webkitGetAsEntry()
      if (item) {
        if (item.isFile) {
          // 如果是文件，直接添加到文件列表
          const file = items[i].getAsFile()
          if (file && !isHiddenFile(file.name)) {
            files.push(file)
          }
        } else if (item.isDirectory) {
          // 如果是文件夹，递归读取文件夹内容
          const folderFiles = await readFolder(item)
          files = [...files, ...folderFiles.filter((file) => !isHiddenFile(file.name))]
        }
      } else {
        // 如果不支持webkitGetAsEntry，使用传统的files方法
        const fileList = Array.from(e.dataTransfer.files).filter((file) => !isHiddenFile(file.name))
        files = fileList
        break // 跳出循环，因为传统方式已经获取了所有文件
      }
    }
  } else {
    // 回退到传统的文件列表方法
    files = Array.from(e.dataTransfer.files).filter((file) => !isHiddenFile(file.name))
  }

  await processFiles(files)
}

/**
 * 粘贴上传入口
 * 由 v-dropUpload 指令在 document 的 paste 事件中取出剪贴板文件后调用，
 * 后续的数量限制、校验、上传与拖拽上传完全一致
 * @param {Array<File>} files - 剪贴板中的文件列表
 * @returns {Promise<void>}
 */
const handleFiles = async (files) => {
  await processFiles(files)
}

/**
 * 统一的文件处理流程：数量限制 -> 校验 -> 上传
 * @param {Array<File>} files - 文件列表
 * @returns {Promise<void>}
 */
const processFiles = async (rawFiles) => {
  let files = rawFiles
  if (!files || files.length === 0) return

  // 已有上传任务时不接新任务：并发上传会让进度提示互相覆盖，上传结果也难以归位
  if (isUploading()) {
    message.warning('有文件正在上传中，请等待当前任务完成后再试')
    return
  }

  // 检查文件数量限制
  if (files.length > props.maxLength) {
    message.error(`单次最多只能上传 ${props.maxLength} 个文件`)
    return
  }

  // 校验与上传期间持有上传锁，异常时也要释放
  lockUpload()
  try {
    // 根据配置处理文件校验逻辑
    let checkFileRef = true
    if (props.before) {
      // 使用自定义校验函数
      const customCheckResult = await props.before(files)
      if (Array.isArray(customCheckResult)) {
        // 返回数组：调用方已自行过滤，用过滤后的结果继续
        files = customCheckResult
        if (!files.length) return
      } else if (!customCheckResult) {
        return
      }
    }
    if (
      props.types.length > 0 ||
      props.maxSizeMB !== null ||
      props.widthScope?.length > 0 ||
      props.heightScope?.length > 0 ||
      props.ratio?.length > 0
    ) {
      // 使用默认校验规则
      checkFileRef = await checkFile(files)
    }

    if (checkFileRef) {
      await upload(files)
    }
  } finally {
    unlockUpload()
  }
}

/**
 * 读取文件夹内容的辅助函数
 * @param {FileSystemDirectoryEntry} directoryEntry - 目录条目
 * @returns {Promise<Array<File>>} 包含所有文件的Promise
 */
const readFolder = async (directoryEntry) => {
  const files = []
  const dirReader = directoryEntry.createReader()
  const entries = await readEntriesRecursive(dirReader)
  // 将所有目录项转换为文件
  for (const entry of entries) {
    if (entry.isFile) {
      const file = await getFileFromEntry(entry, entry.fullPath)
      if (file) {
        files.push(file)
      }
    }
  }
  return files
}

/**
 * 递归读取目录条目
 * @param {FileSystemDirectoryReader} dirReader - 目录读取器
 * @returns {Promise<Array<FileSystemEntry>>} 目录项数组
 */
const readEntriesRecursive = (dirReader) => {
  return new Promise((resolve) => {
    const entries = []
    const readEntries = () => {
      dirReader.readEntries((results) => {
        if (results.length === 0) {
          // 没有更多的条目，返回结果
          resolve(entries)
        } else {
          // 添加当前批次的条目
          for (let i = 0; i < results.length; i++) {
            entries.push(results[i])

            // 如果是目录，递归读取其内容
            if (results[i].isDirectory) {
              const subDirReader = results[i].createReader()
              const subDirPromises = readEntriesRecursive(subDirReader)

              // 等待子目录读取完成并将结果合并
              subDirPromises.then((subEntries) => {
                entries.push(...subEntries)
              })
            }
          }

          // 继续读取下一批条目
          readEntries()
        }
      })
    }
    readEntries()
  })
}

/**
 * 判断是否为隐藏文件
 * @param {string} fileName - 文件名
 * @returns {boolean} 是否为隐藏文件
 */
const isHiddenFile = (fileName) => {
  return fileName.startsWith('.')
}

/**
 * 从文件系统条目获取文件对象
 * @param {FileSystemFileEntry} fileEntry - 文件系统文件条目
 * @param {String} relativePath - 文件相对于根目录的路径
 * @returns {Promise<File>} 文件对象
 */
const getFileFromEntry = (fileEntry, path) => {
  return new Promise((resolve) => {
    fileEntry.file(
      (file) => {
        // 检查是否为隐藏文件，如果是则不处理
        if (isHiddenFile(file.name)) {
          resolve(null)
          return
        }
        // 使用 Object.defineProperty 添加自定义属性，因为它无法直接设置
        Object.defineProperty(file, 'webkitRelativePath', {
          value: path,
          writable: false, // 只读
          configurable: true,
          enumerable: true
        })
        resolve(file)
      },
      (error) => {
        console.error('读取文件错误:', error)
        resolve(null)
      }
    )
  })
}

/**
 * 执行文件上传
 * @param {Array<File>} files - 文件列表
 */
const upload = async (files) => {
  // 如果不用内置上传就直接抛出校验后的文件数组
  if (!props.useUpload) {
    emits('success', files)
    return
  }

  // 依次上传每个文件并收集结果
  const results = []
  uploadStates.total = files.length
  uploadStates.success = 0
  emits('uploading', true)

  const showProgress = () => {
    if (props.silent) return
    message.loading({
      content: () => `上传中...（${uploadStates.success} / ${uploadStates.total}）`,
      duration: 0
    })
  }
  showProgress()

  try {
    for (const file of files) {
      try {
        const response = await uploadHandle(file)
        results.push(response) // 将成功上传的文件添加到结果列表
        uploadStates.success++

        // 更新进度信息：销毁再开一条，内容才会刷新
        if (!props.silent) {
          message.destroy()
          showProgress()
        }
      } catch (err) {
        const errorMsg = `文件 "${file.name}" 上传失败`
        message.error(errorMsg)
        emits('error', { message: errorMsg, file, error: err })
      }
    }

    // 统一返回所有上传成功的文件
    emits('success', results)
    if (!props.silent) {
      message.destroy() // 清除loading消息
      message.success('上传成功')
    }
  } finally {
    /* 结束态放在 finally 里：上面任何一步抛了（或调用方的 success 回调里抛了），
       调用方的「上传中」也必须收掉，否则界面会一直挂着转圈 */
    emits('uploading', false)
  }
}

// 上传方法
const uploadHandle = (file) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)

    // 根据文件扩展名设置bizType
    const bizType = getBizTypeByExtension(file.name)
    formData.append('bizType', bizType)

    uploadFile(formData)
      .then((res) => {
        if (res.data) {
          resolve(res.data)
        } else {
          reject(new Error('上传失败'))
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
}

// 暴露给 v-dropUpload 指令：粘贴上传时由指令直接调用
defineExpose({ handleFiles })
</script>

<style scoped>
.uploadMask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7); /* 半透明白色，必须有 */
  backdrop-filter: blur(8px); /* 高斯模糊 8px，穿透模糊背景 */
  z-index: 999;
  border: 2px dashed #ccc;
  border-radius: 6px;
  padding: 0px 20px;
  display: none;
}
.uploadMask-content {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.uploadMask-content .icon {
  font-size: 20px;
  margin-right: 10px;
}
.uploadMask-text {
  font-size: 18px;
}
.uploadMask-tips {
  font-size: 16px;
  color: #ccc;
  margin-top: 6px;
  text-align: center;
}
</style>
